import type { IShoppingItem, IShoppingList } from '@inter-ikea-kompis/types';

import ServiceFetch from '../../../../utilities/ServiceFetch.js';

/**
 * This class handles adding items to the China shopping list.
 */
export default class ChinaShoppingListService {
	private readonly endpoint: string;

	/**
	 * Constructor.
	 *
	 * @param options Options.
	 * @param options.endpoint Endpoint.
	 */
	constructor(options: { endpoint: string }) {
		this.endpoint = options.endpoint;
	}

	/**
	 * Adds items to shopping list.
	 *
	 * @param options Options.
	 * @param options.items Array of shopping items.
	 * @param options.token Token.
	 * @returns Promise object.
	 */
	public async addToList(options: {
		items: IShoppingItem[];
		token: string;
	}): Promise<IShoppingList> {
		await ServiceFetch.text(`${this.endpoint}/add-products-with-quantity`, {
			method: 'post',
			credentials: 'same-origin',
			headers: this.getHeaders(options.token),
			body: this.getAddToListBody(options.items)
		});

		const listCount = await this.getListCount(options);

		return {
			listCount,
			errorList: []
		};
	}

	/**
	 * Returns list count for a shopping list.
	 *
	 * @param options Options.
	 * @param options.token Token.
	 * @returns Promise object.
	 */
	public async getListCount(options: { token: string }): Promise<number> {
		const response = await ServiceFetch.json(this.endpoint, {
			method: 'get',
			credentials: 'same-origin',
			headers: this.getHeaders(options.token)
		});

		return response.length || 0;
	}

	/**
	 * Returns the body.
	 *
	 * @param items Items.
	 * @returns Add to cart body.
	 */
	private getAddToListBody(items: IShoppingItem[]): string {
		return JSON.stringify({
			products: items.map((item) => ({
				productFullId: (item.type.toUpperCase() === 'SPR' ? 's' : '') + item.id,
				quantity: item.quantity
			}))
		});
	}

	/**
	 * Returns headers.
	 *
	 * @param token Token.
	 * @returns Headers.
	 */
	private getHeaders(token: string): HeadersInit {
		const headers = {
			'content-type': 'application/json',
			'x-client-channel': 'WEB',
			'x-client-platform': 'PcWeb',
			authorization: 'Bearer ' + token
		};

		return headers;
	}
}
